import { CSSProperties, memo, useEffect, useState } from 'react'
import { styx, makeKey } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import TagSymphony, { TagColors } from 'elements/TagSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import WebsiteBuilderPhonePreview from 'components/websiteBuilder/WebsiteBuilderPhonePreview'
import useBrand from 'Hooks/useBrand'
import useWebsiteBuilder from 'Hooks/useWebsiteBuilder'
import { WebsiteBuilderTemplateType, WEBSITE_TEMPLATE_INFO, WebsiteTemplateCategory, WEBSITE_TEMPLATE_CATEGORIES } from 'modules/Const'
import { calculateThemeColors } from 'modules/Utils'
import { getPalette } from 'react-palette'

const WEBSITE_PREVIEW_ZOOM = 0.5;

interface AssistantWorkspaceTemplateSelectionProps {
    workspaceContent: {
        type: 'report' | 'preview' | 'component' | 'custom' | 'website' | 'templateSelection';
        data: any;
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: { templateData?: any }) => void;
}

// Category mapping utilities
const mapCategoryNameToConstant = (categoryName: string): string => {
    const mapping: Record<string, string> = {
        'popular': WebsiteTemplateCategory.POPULAR,
        'music': WebsiteTemplateCategory.MUSIC,
        'events': WebsiteTemplateCategory.EVENTS,
        'collectData': WebsiteTemplateCategory.COLLECT_DATA,
        'gallery': WebsiteTemplateCategory.GALLERY
    };
    return mapping[categoryName] || WebsiteTemplateCategory.POPULAR;
};

const mapConstantToCategoryName = (constant: string): string => {
    const mapping: Record<string, string> = {
        [WebsiteTemplateCategory.POPULAR]: 'popular',
        [WebsiteTemplateCategory.MUSIC]: 'music', 
        [WebsiteTemplateCategory.EVENTS]: 'events',
        [WebsiteTemplateCategory.COLLECT_DATA]: 'collectData',
        [WebsiteTemplateCategory.GALLERY]: 'gallery'
    };
    return mapping[constant] || 'popular';
};

const AssistantWorkspaceTemplateSelection = ({
    workspaceContent,
    handleSendMessage
}: AssistantWorkspaceTemplateSelectionProps) => {
    const { currentBrand, getBrandImage } = useBrand()
    const { getTemplate } = useWebsiteBuilder()
    
    // Extract allowed categories from workspace data
    const { data } = workspaceContent; // data here is activeWorkspace.data from useMaestroChat
    const allowedCategories = data?.templateCategories || [];
    const isDemoMode = data?.isDemo || false; // Check if it's a UI test demo

    // Logic to set initial category based on allowed categories
    const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>(() => {
        if (allowedCategories.length === 1) {
            return mapCategoryNameToConstant(allowedCategories[0]);
        }
        // If multiple allowed, default to the first one, otherwise POPULAR
        return allowedCategories.length > 0 ? mapCategoryNameToConstant(allowedCategories[0]) : WebsiteTemplateCategory.POPULAR;
    });
    
    const [websiteStyleColors, setWebsiteStyleColors] = useState<{
        backgroundColor: string;
        secondaryColor: string;
        textColor: string;
    } | null>(null)
    const [isInitialized, setIsInitialized] = useState(false)

    // Initialize colors based on brand image
    useEffect(() => {
        const initializeColors = async () => {
            try {
                const image = getBrandImage();
                if (image) {
                    const paletteData = await getPalette(image);
                    const { backgroundColor, secondaryColor, textColor } = calculateThemeColors(paletteData);
                    setWebsiteStyleColors({ backgroundColor, secondaryColor, textColor });
                }
                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to initialize colors:', error);
                setIsInitialized(true);
            }
        };

        initializeColors();
    }, [getBrandImage]);

    // Update selected category when allowed categories change
    useEffect(() => {
        if (isDemoMode) { // Only adjust for demo mode based on passed categories
            if (allowedCategories.length === 1) {
                setSelectedTemplateCategory(mapCategoryNameToConstant(allowedCategories[0]));
            } else if (allowedCategories.length > 0) { // If multiple allowed, default to the first one
                setSelectedTemplateCategory(mapCategoryNameToConstant(allowedCategories[0]));
            } else { // If no specific categories for demo, default to POPULAR
                setSelectedTemplateCategory(WebsiteTemplateCategory.POPULAR);
            }
        }
        // For non-demo, or if allowedCategories is empty, it might rely on other logic or default.
        // This ensures UI test mode correctly respects `templateCategories`.
    }, [allowedCategories, isDemoMode]);

    const handleTemplateSelect = (templateType: WebsiteBuilderTemplateType) => {
        const template = getTemplate({ templateType });
        if (!template) {
            console.warn('Template not found:', templateType);
            return;
        }

        // Send template data to AI
        const templateData = {
            templateType,
            title: WEBSITE_TEMPLATE_INFO[templateType].title,
            websiteContent: template.websiteContent,
            websiteStyle: template.websiteStyle,
            category: WEBSITE_TEMPLATE_INFO[templateType].websiteTemplateCategory
        };

        // Send user-friendly message with hidden template data
        const userFriendlyMessage = `I want to use the "${WEBSITE_TEMPLATE_INFO[templateType].title}" template to build my website.`;
        handleSendMessage(userFriendlyMessage, { templateData });
    };

    const renderTemplateCategories = () => {
        // Don't show category selector if only one category specified
        if (allowedCategories.length === 1) {
            return null; // Hide the category selector completely
        }
        
        // Filter categories to only show allowed ones
        const filteredCategories = WEBSITE_TEMPLATE_CATEGORIES.filter(category => 
            allowedCategories.length === 0 || // Show all if no filter
            allowedCategories.includes(mapConstantToCategoryName(category.name))
        );

        return (
            <div className="assistantTemplateCategories" style={sty.assistantTemplateCategories}>
                {filteredCategories.map((template, index) => {
                    const { name, title, icon, color, iconColor } = template;
                    const isSelected = selectedTemplateCategory === name;

                    return (
                        <TagSymphony
                            key={makeKey('templateCategory', name, index)}
                            className="assistantTemplateCategoryTag"
                            onClick={() => setSelectedTemplateCategory(name)}
                            title={title}
                            icon={icon}
                            color={isSelected ? color : TagColors.grey}
                            iconColor={isSelected ? iconColor : undefined}
                        />
                    );
                })}
            </div>
        );
    };

    const renderTemplates = () => {
        if (selectedTemplateCategory === WebsiteTemplateCategory.GALLERY) {
            return (
                <div style={sty.assistantTemplateGalleryPlaceholder}>
                    <Icon className="assistantTemplateGalleryIcon" icon="image" size={48} style={{ color: Colors.greyDark }} />
                    <div style={{ fontSize: 16, color: Colors.greyDark, textAlign: 'center' }}>
                        Gallery templates coming soon!
                    </div>
                </div>
            );
        }

        const filteredTemplates = Object.entries(WEBSITE_TEMPLATE_INFO)
            .filter(([templateType]) => {
                const websiteTemplateType = templateType as WebsiteBuilderTemplateType;
                const templateInfo = WEBSITE_TEMPLATE_INFO[websiteTemplateType];

                if (templateInfo.isDisabled) return false;

                if (selectedTemplateCategory === WebsiteTemplateCategory.ALL_TEMPLATES) return true;
                if (selectedTemplateCategory === WebsiteTemplateCategory.POPULAR) return templateInfo.isPopular;

                const templateCategory = templateInfo.websiteTemplateCategory;
                switch (selectedTemplateCategory) {
                    case WebsiteTemplateCategory.MUSIC:
                        return templateCategory === WebsiteTemplateCategory.MUSIC;
                    case WebsiteTemplateCategory.EVENTS:
                        return templateCategory === WebsiteTemplateCategory.EVENTS;
                    case WebsiteTemplateCategory.COLLECT_DATA:
                        return templateCategory === WebsiteTemplateCategory.COLLECT_DATA;
                    default:
                        return false;
                }
            })
            .sort(([a], [b]) => {
                if (selectedTemplateCategory === WebsiteTemplateCategory.POPULAR) {
                    const aInfo = WEBSITE_TEMPLATE_INFO[a as WebsiteBuilderTemplateType];
                    const bInfo = WEBSITE_TEMPLATE_INFO[b as WebsiteBuilderTemplateType];
                    return (aInfo.popularOrder || 0) - (bInfo.popularOrder || 0);
                }
                return 0;
            });

        return (
            <div className="assistantTemplateGrid" style={sty.assistantTemplateGrid}>
                {filteredTemplates.map(([templateType], index) => {
                    const websiteTemplateType = templateType as WebsiteBuilderTemplateType;
                    const template = getTemplate({ templateType: websiteTemplateType });

                    if (!template || !isInitialized) return null;

                    const templateInfo = WEBSITE_TEMPLATE_INFO[websiteTemplateType];
                    const { title, isFree } = templateInfo;
                    const { websiteContent, websiteStyle: templateStyle } = template;

                    // Apply brand colors to template style
                    let finalStyle = templateStyle;
                    if (websiteStyleColors) {
                        finalStyle = { ...templateStyle, ...websiteStyleColors };
                    }

                    return (
                        <div
                            key={makeKey('template', websiteTemplateType, index)}
                            className="assistantTemplateItem"
                            style={sty.assistantTemplateItem}
                        >
                            <div className="assistantTemplateTitle" style={sty.assistantTemplateTitle}>
                                <span>{title}</span>
                                {!isFree && (
                                    <Icon
                                        className="assistantTemplateGemIcon"
                                        icon="gem"
                                        size={14}
                                        style={{ color: Colors.purple }}
                                    />
                                )}
                            </div>
                            
                            <div className="assistantTemplatePreview" style={sty.assistantTemplatePreview}>
                                <WebsiteBuilderPhonePreview
                                    className="assistantTemplatePhonePreview"
                                    style={{ zoom: WEBSITE_PREVIEW_ZOOM }}
                                    websiteContent={websiteContent}
                                    websiteStyle={finalStyle}
                                    isSticky={false}
                                    isFooter={false}
                                    isLocalTabLogic
                                />
                            </div>

                            <ButtonSymphony
                                className="assistantTemplateSelectButton"
                                onClick={() => handleTemplateSelect(websiteTemplateType)}
                                size="small"
                                style={sty.assistantTemplateSelectButton}
                            >
                                Use Template
                            </ButtonSymphony>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!isInitialized) {
        return (
            <div className="assistantTemplateSelection" style={sty.assistantTemplateSelection}>
                <div style={sty.assistantTemplateLoadingState}>
                    <Icon className="assistantTemplateLoadingIcon" icon="website" size={48} style={{ color: Colors.greyDark }} />
                    <div style={{ fontSize: 16, color: Colors.greyDark }}>
                        Loading templates...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="assistantTemplateSelection" style={sty.assistantTemplateSelection}>
            {/* Header - Skip Templates button removed completely */}
            <div className="assistantTemplateHeader" style={sty.assistantTemplateHeader}>
                <div className="assistantTemplateHeaderText" style={sty.assistantTemplateHeaderText}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Choose a template</h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: 14, color: Colors.greyDark }}>
                        Pick a professional design to get started
                    </p>
                </div>
            </div>

            {/* Template categories - conditionally rendered */}
            {renderTemplateCategories()}

            {/* Template grid */}
            <div className="assistantTemplateContent" style={sty.assistantTemplateContent}>
                {renderTemplates()}
            </div>
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    assistantTemplateSelection: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    assistantTemplateLoadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 12,
    },
    assistantTemplateHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.grey}`,
    },
    assistantTemplateHeaderText: {
        flex: 1,
    },
    assistantTemplateCategories: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    assistantTemplateContent: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px 0',
    },
    assistantTemplateGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16,
    },
    assistantTemplateItem: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: Colors.greyLighter,
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${Colors.grey}`,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    assistantTemplateTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: 'white',
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    assistantTemplatePreview: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        backgroundColor: Colors.greyLight,
        minHeight: 120,
    },
    assistantTemplateSelectButton: {
        margin: 12,
        marginTop: 0,
    },
    assistantTemplateGalleryPlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 12,
    },
};

export default memo(AssistantWorkspaceTemplateSelection); 